/**
 * StructureChecklist Component
 *
 * Displays the structure analysis results in a kid-friendly format.
 * Supports three display modes to prevent cognitive overload:
 *
 * - summary: 2 wins + 1 next step (default for results)
 * - coach: shows "maybe" with explanations, encouraging tone
 * - details: full checklist + evidence snippets
 *
 * Used by Challenge Results, Revision Studio, and Story Detective.
 */

import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Check, HelpCircle, Plus, ChevronDown, ChevronUp, Sparkles, ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui'
import { cn } from '@/lib/utils'
import {
  type StructureAnalysisResult,
  type StoryElement,
  type DetectionStatus,
  STORY_ELEMENTS,
  STORY_ELEMENT_INFO,
} from '@/types/domain'
import { getStatusLabel } from '@/services/structureAnalyzer'

// =============================================================================
// Types
// =============================================================================

export type ChecklistMode = 'summary' | 'coach' | 'details'

interface StructureChecklistProps {
  analysis: StructureAnalysisResult
  /** Display mode: summary (2 wins + 1 tip), coach (maybes explained), details (full list) */
  mode?: ChecklistMode
  /** Allow user to expand from summary to details */
  expandable?: boolean
  /** Highlight specific elements (for comparison view) */
  highlightElements?: StoryElement[]
  /** Callback when user wants to improve a specific element */
  onImproveElement?: (element: StoryElement) => void
  className?: string
}

// =============================================================================
// Main Component
// =============================================================================

export function StructureChecklist({
  analysis,
  mode = 'summary',
  expandable = true,
  highlightElements,
  onImproveElement,
  className,
}: StructureChecklistProps) {
  const [currentMode, setCurrentMode] = useState<ChecklistMode>(mode)

  // Categorize elements by status
  const found = STORY_ELEMENTS.filter(e => analysis.present[e] === 'found')
  const maybe = STORY_ELEMENTS.filter(e => analysis.present[e] === 'maybe')
  const missing = STORY_ELEMENTS.filter(e => analysis.present[e] === 'missing')

  // Get the "next best improvement" - prioritize maybe over missing
  const nextImprovement = maybe[0] || missing[0]
  const nextSuggestion = nextImprovement
    ? analysis.suggestions.find(s => s.element === nextImprovement)
    : null

  return (
    <div className={cn('space-y-4', className)}>
      <AnimatePresence mode="wait">
        {currentMode === 'summary' && (
          <SummaryMode
            key="summary"
            found={found}
            nextImprovement={nextImprovement}
            nextSuggestion={nextSuggestion?.message}
            onImprove={onImproveElement}
          />
        )}

        {currentMode === 'coach' && (
          <CoachMode
            key="coach"
            analysis={analysis}
            found={found}
            maybe={maybe}
            missing={missing}
            highlightElements={highlightElements}
          />
        )}

        {currentMode === 'details' && (
          <DetailsMode
            key="details"
            analysis={analysis}
            highlightElements={highlightElements}
          />
        )}
      </AnimatePresence>

      {/* Mode toggle */}
      {expandable && (
        <div className="flex justify-center gap-2 pt-2">
          {currentMode !== 'details' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCurrentMode(currentMode === 'summary' ? 'coach' : 'details')}
              className="text-[var(--color-kids-text-muted)]"
            >
              {currentMode === 'summary' ? 'Show more' : 'See all details'}
              <ChevronDown className="w-4 h-4 ml-1" />
            </Button>
          )}
          {currentMode !== 'summary' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCurrentMode(currentMode === 'details' ? 'coach' : 'summary')}
              className="text-[var(--color-kids-text-muted)]"
            >
              {currentMode === 'details' ? 'Show less' : 'Quick summary'}
              <ChevronUp className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

// =============================================================================
// Summary Mode: 2 wins + 1 next step
// =============================================================================

interface SummaryModeProps {
  found: StoryElement[]
  nextImprovement?: StoryElement
  nextSuggestion?: string
  onImprove?: (element: StoryElement) => void
}

function SummaryMode({ found, nextImprovement, nextSuggestion, onImprove }: SummaryModeProps) {
  const topWins = found.slice(0, 2)

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="space-y-4"
    >
      {/* Wins */}
      {topWins.length > 0 && (
        <div className="p-4 rounded-xl bg-[var(--color-kids-success)]/10 border border-[var(--color-kids-success)]/20">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-[var(--color-kids-success)]" />
            <span className="font-display font-bold text-[var(--color-kids-success)]">
              Nice work!
            </span>
          </div>
          <div className="space-y-2">
            {topWins.map(element => {
              const info = STORY_ELEMENT_INFO[element]
              return (
                <div key={element} className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[var(--color-kids-success)]" />
                  <span className="text-lg">{info.emoji}</span>
                  <span className="text-[var(--color-kids-text)]">
                    Your story has a clear <strong>{info.label.toLowerCase()}</strong>
                  </span>
                </div>
              )
            })}
            {found.length > 2 && (
              <p className="text-sm text-[var(--color-kids-success)] pl-6">
                + {found.length - 2} more elements found!
              </p>
            )}
          </div>
        </div>
      )}

      {/* Next improvement */}
      {nextImprovement && (
        <div className="p-4 rounded-xl bg-[var(--color-kids-primary)]/10 border border-[var(--color-kids-primary)]/20">
          <div className="flex items-center gap-2 mb-2">
            <ArrowRight className="w-5 h-5 text-[var(--color-kids-primary)]" />
            <span className="font-display font-bold text-[var(--color-kids-primary)]">
              Level up your story
            </span>
          </div>
          <p className="text-[var(--color-kids-text)] mb-3">
            {nextSuggestion || `Try adding a ${STORY_ELEMENT_INFO[nextImprovement].label.toLowerCase()} to make your story even better!`}
          </p>
          {onImprove && (
            <Button
              size="sm"
              onClick={() => onImprove(nextImprovement)}
            >
              Improve this
            </Button>
          )}
        </div>
      )}

      {/* All found - celebration! */}
      {found.length === STORY_ELEMENTS.length && (
        <div className="p-4 rounded-xl bg-gradient-to-r from-[var(--color-kids-accent)]/20 to-[var(--color-kids-success)]/20 border border-[var(--color-kids-accent)]/30 text-center">
          <div className="text-4xl mb-2">🌟</div>
          <p className="font-display font-bold text-[var(--color-kids-text)]">
            Amazing! Your story has all the key elements!
          </p>
        </div>
      )}
    </motion.div>
  )
}

// =============================================================================
// Coach Mode: Shows maybes with explanations
// =============================================================================

interface CoachModeProps {
  analysis: StructureAnalysisResult
  found: StoryElement[]
  maybe: StoryElement[]
  missing: StoryElement[]
  highlightElements?: StoryElement[]
}

function CoachMode({ analysis, found, maybe, missing, highlightElements }: CoachModeProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="space-y-4"
    >
      {/* Found elements - compact */}
      {found.length > 0 && (
        <div className="p-3 rounded-xl bg-[var(--color-kids-success)]/10 border border-[var(--color-kids-success)]/20">
          <div className="flex items-center gap-2 flex-wrap">
            <Check className="w-4 h-4 text-[var(--color-kids-success)]" />
            <span className="text-sm font-medium text-[var(--color-kids-success)]">Found:</span>
            {found.map(element => (
              <span
                key={element}
                className="text-sm bg-[var(--color-kids-success)]/20 text-[var(--color-kids-text)] px-2 py-0.5 rounded-full"
              >
                {STORY_ELEMENT_INFO[element].emoji} {STORY_ELEMENT_INFO[element].label}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Maybe elements - with explanations */}
      {maybe.length > 0 && (
        <div className="space-y-3">
          <p className="text-sm font-medium text-[var(--color-kids-warning)]">
            I think I spotted these, but they could be clearer:
          </p>
          {maybe.map(element => {
            const info = STORY_ELEMENT_INFO[element]
            const note = analysis.notes.find(n => n.element === element)
            const suggestion = analysis.suggestions.find(s => s.element === element)
            const isHighlighted = highlightElements?.includes(element)

            return (
              <div
                key={element}
                className={cn(
                  'p-3 rounded-xl bg-[var(--color-kids-warning)]/10 border border-[var(--color-kids-warning)]/20',
                  isHighlighted && 'ring-2 ring-[var(--color-kids-primary)]'
                )}
              >
                <div className="flex items-center gap-2 mb-1">
                  <HelpCircle className="w-4 h-4 text-[var(--color-kids-warning)]" />
                  <span className="text-lg">{info.emoji}</span>
                  <span className="font-medium text-[var(--color-kids-text)]">{info.label}</span>
                </div>
                {note?.excerpt && (
                  <p className="text-sm text-[var(--color-kids-text-muted)] italic pl-6 mb-2">
                    "{note.excerpt}"
                  </p>
                )}
                {suggestion && (
                  <p className="text-sm text-[var(--color-kids-text)] pl-6">
                    {suggestion.message}
                  </p>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Missing elements - encouraging */}
      {missing.length > 0 && (
        <div className="p-3 rounded-xl bg-[var(--color-kids-surface-alt)] border border-[var(--color-kids-text-light)]/20">
          <div className="flex items-center gap-2 mb-2">
            <Plus className="w-4 h-4 text-[var(--color-kids-text-muted)]" />
            <span className="text-sm font-medium text-[var(--color-kids-text-muted)]">
              You could add:
            </span>
          </div>
          <div className="flex flex-wrap gap-2 pl-6">
            {missing.map(element => (
              <span
                key={element}
                className="text-sm bg-[var(--color-kids-surface)] text-[var(--color-kids-text-muted)] px-2 py-0.5 rounded-full"
              >
                {STORY_ELEMENT_INFO[element].emoji} {STORY_ELEMENT_INFO[element].label}
              </span>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  )
}

// =============================================================================
// Details Mode: Full checklist with all info
// =============================================================================

interface DetailsModeProps {
  analysis: StructureAnalysisResult
  highlightElements?: StoryElement[]
}

function DetailsMode({ analysis, highlightElements }: DetailsModeProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="space-y-2"
    >
      {STORY_ELEMENTS.map((element, index) => {
        const status = analysis.present[element]
        const info = STORY_ELEMENT_INFO[element]
        const note = analysis.notes.find(n => n.element === element)
        const suggestion = analysis.suggestions.find(s => s.element === element)
        const isHighlighted = highlightElements?.includes(element)

        return (
          <motion.div
            key={element}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05 }}
          >
            <DetailItem
              status={status}
              emoji={info.emoji}
              label={info.label}
              description={info.description}
              excerpt={note?.excerpt}
              suggestion={suggestion?.message}
              highlighted={isHighlighted}
            />
          </motion.div>
        )
      })}
    </motion.div>
  )
}

// =============================================================================
// Detail Item (used in Details mode)
// =============================================================================

interface DetailItemProps {
  status: DetectionStatus
  emoji: string
  label: string
  description: string
  excerpt?: string
  suggestion?: string
  highlighted?: boolean
}

function DetailItem({
  status,
  emoji,
  label,
  description,
  excerpt,
  suggestion,
  highlighted,
}: DetailItemProps) {
  const statusConfig = {
    found: {
      icon: Check,
      iconColor: 'text-[var(--color-kids-success)]',
      bgColor: 'bg-[var(--color-kids-success)]/10',
      borderColor: 'border-[var(--color-kids-success)]/30',
      textColor: 'text-[var(--color-kids-text)]',
    },
    maybe: {
      icon: HelpCircle,
      iconColor: 'text-[var(--color-kids-warning)]',
      bgColor: 'bg-[var(--color-kids-warning)]/10',
      borderColor: 'border-[var(--color-kids-warning)]/30',
      textColor: 'text-[var(--color-kids-text)]',
    },
    missing: {
      icon: Plus,
      iconColor: 'text-[var(--color-kids-text-muted)]',
      bgColor: 'bg-[var(--color-kids-surface-alt)]',
      borderColor: 'border-[var(--color-kids-text-light)]/30',
      textColor: 'text-[var(--color-kids-text-muted)]',
    },
  }

  const config = statusConfig[status]
  const Icon = config.icon

  return (
    <div
      className={cn(
        'rounded-[var(--radius-kid)] border-2 transition-all p-3',
        config.bgColor,
        config.borderColor,
        highlighted && 'ring-2 ring-[var(--color-kids-primary)] ring-offset-2'
      )}
    >
      <div className="flex items-start gap-3">
        {/* Status Icon */}
        <div
          className={cn(
            'flex-shrink-0 rounded-full p-1',
            status === 'found' && 'bg-[var(--color-kids-success)]/20',
            status === 'maybe' && 'bg-[var(--color-kids-warning)]/20',
            status === 'missing' && 'bg-[var(--color-kids-surface)]'
          )}
        >
          <Icon className={cn('w-4 h-4', config.iconColor)} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-lg">{emoji}</span>
            <span className={cn('font-display font-semibold', config.textColor)}>
              {label}
            </span>
            <span
              className={cn(
                'text-xs px-2 py-0.5 rounded-full font-medium',
                status === 'found' && 'bg-[var(--color-kids-success)]/20 text-[var(--color-kids-success-dark)]',
                status === 'maybe' && 'bg-[var(--color-kids-warning)]/20 text-[var(--color-kids-warning)]',
                status === 'missing' && 'bg-[var(--color-kids-surface)] text-[var(--color-kids-text-muted)]'
              )}
            >
              {getStatusLabel(status)}
            </span>
          </div>

          <p className="text-sm text-[var(--color-kids-text-muted)] mt-0.5">
            {description}
          </p>

          {/* Evidence excerpt */}
          {excerpt && (
            <p className="text-sm italic text-[var(--color-kids-text-muted)] mt-2 pl-2 border-l-2 border-[var(--color-kids-primary)]/30">
              "{excerpt}"
            </p>
          )}

          {/* Suggestion */}
          {suggestion && status !== 'found' && (
            <p className="text-sm mt-2 p-2 rounded-lg bg-[var(--color-kids-surface)] border border-[var(--color-kids-primary)]/20 text-[var(--color-kids-text)]">
              {suggestion}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// Score Display Component
// =============================================================================

interface ScoreDisplayProps {
  score: number
  className?: string
}

export function ScoreDisplay({ score, className }: ScoreDisplayProps) {
  const getScoreColor = (score: number) => {
    if (score >= 80) return 'var(--color-kids-success)'
    if (score >= 50) return 'var(--color-kids-warning)'
    return 'var(--color-kids-secondary)'
  }

  const getScoreLabel = (score: number) => {
    if (score >= 80) return 'Great structure!'
    if (score >= 60) return 'Good progress!'
    if (score >= 40) return 'Getting there!'
    return 'Keep building!'
  }

  return (
    <div className={cn('text-center', className)}>
      <div className="relative inline-flex items-center justify-center">
        <svg className="w-24 h-24 transform -rotate-90">
          <circle
            cx="48"
            cy="48"
            r="40"
            stroke="var(--color-kids-surface-alt)"
            strokeWidth="8"
            fill="none"
          />
          <circle
            cx="48"
            cy="48"
            r="40"
            stroke={getScoreColor(score)}
            strokeWidth="8"
            fill="none"
            strokeDasharray={`${(score / 100) * 251.2} 251.2`}
            strokeLinecap="round"
            className="transition-all duration-1000"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-display font-bold text-[var(--color-kids-text)]">
            {score}
          </span>
          <span className="text-xs text-[var(--color-kids-text-muted)]">
            / 100
          </span>
        </div>
      </div>
      <p className="mt-2 font-display font-semibold text-[var(--color-kids-text)]">
        {getScoreLabel(score)}
      </p>
    </div>
  )
}

// =============================================================================
// Compact Checklist for inline use
// =============================================================================

interface CompactChecklistProps {
  analysis: StructureAnalysisResult
  className?: string
}

export function CompactChecklist({ analysis, className }: CompactChecklistProps) {
  return (
    <div className={cn('flex flex-wrap gap-2', className)}>
      {STORY_ELEMENTS.map(element => {
        const status = analysis.present[element]
        const info = STORY_ELEMENT_INFO[element]

        return (
          <span
            key={element}
            className={cn(
              'text-sm px-2 py-1 rounded-full flex items-center gap-1',
              status === 'found' && 'bg-[var(--color-kids-success)]/10 text-[var(--color-kids-success)]',
              status === 'maybe' && 'bg-[var(--color-kids-warning)]/10 text-[var(--color-kids-warning)]',
              status === 'missing' && 'bg-[var(--color-kids-surface-alt)] text-[var(--color-kids-text-muted)]'
            )}
            title={`${info.label}: ${getStatusLabel(status)}`}
          >
            {status === 'found' && <Check className="w-3 h-3" />}
            {status === 'maybe' && <HelpCircle className="w-3 h-3" />}
            <span>{info.emoji}</span>
          </span>
        )
      })}
    </div>
  )
}

export default StructureChecklist
